/**
 * Standardized error messages for Financial Settings pages
 * @module constants/error-messages
 *
 * Provides consistent, user-friendly error messages across all budget management pages
 * Following architecture guidelines from monumenta-financial-settings-architecture.md
 */

/**
 * Network and connectivity error messages
 */
export const NETWORK_ERRORS = {
  en: 'Unable to connect. Please check your internet connection and try again.',
  ptBR: 'Não foi possível conectar. Verifique sua conexão com a internet e tente novamente.'
} as const

/**
 * Authentication error messages (401)
 * User session expired - need to redirect to login
 */
export const AUTHENTICATION_ERRORS = {
  en: 'Your session has expired. Please sign in again.',
  ptBR: 'Sua sessão expirou. Por favor, faça login novamente.',
  action: 'redirect-to-login' // Auto-redirect handled by axios interceptor
} as const

/**
 * Authorization error messages (403)
 * User doesn't have director role
 */
export const AUTHORIZATION_ERRORS = {
  en: "You don't have permission to access this feature. Contact your administrator.",
  ptBR: 'Você não tem permissão para acessar este recurso. Entre em contato com seu administrador.',
  action: 'show-toast' // Show error toast only
} as const

/**
 * Not Found error messages (404)
 * Budget data doesn't exist for the specified year/client
 */
export const NOT_FOUND_ERRORS = {
  // Generic not found
  generic: {
    en: 'Resource not found.',
    ptBR: 'Recurso não encontrado.'
  },
  // Budget specific (with year placeholder)
  budget: {
    en: (year: number | string) => `Budget data not found for ${year}. Create a new budget to get started.`,
    ptBR: (year: number | string) => `Dados de orçamento não encontrados para ${year}. Crie um novo orçamento para começar.`
  },
  // Client budget specific
  clientBudget: {
    en: (clientCode: string, year: number) => `No budget found for client ${clientCode} in ${year}.`,
    ptBR: (clientCode: string, year: number) => `Nenhum orçamento encontrado para o cliente ${clientCode} em ${year}.`
  },
  // Training budget specific
  trainingBudget: {
    en: (year: number) => `No training budget found for ${year}.`,
    ptBR: (year: number) => `Nenhum orçamento de treinamento encontrado para ${year}.`
  }
} as const

/**
 * Server error messages (500)
 * Internal server errors - retriable
 */
export const SERVER_ERRORS = {
  en: 'An unexpected error occurred. Please try again or contact support.',
  ptBR: 'Ocorreu um erro inesperado. Por favor, tente novamente ou entre em contato com o suporte.',
  action: 'retry' // Show retry button
} as const

/**
 * Validation error messages (400)
 * Client-side or server-side validation failures
 */
export const VALIDATION_ERRORS = {
  // Generic validation error
  generic: {
    en: 'Invalid data. Please check your input and try again.',
    ptBR: 'Dados inválidos. Por favor, verifique os campos e tente novamente.'
  },
  // Field-specific validation errors
  fieldErrors: {
    revenue: {
      required: {
        en: 'Revenue is required',
        ptBR: 'Receita é obrigatória'
      },
      positive: {
        en: 'Revenue must be greater than 0',
        ptBR: 'Receita deve ser maior que 0'
      },
      tooLarge: {
        en: 'Revenue value is too large',
        ptBR: 'Valor da receita é muito grande'
      }
    },
    costs: {
      positive: {
        en: 'Costs must be 0 or greater',
        ptBR: 'Custos devem ser 0 ou maior'
      },
      tooLarge: {
        en: 'Costs value is too large',
        ptBR: 'Valor dos custos é muito grande'
      }
    },
    tax: {
      positive: {
        en: 'Tax must be 0 or greater',
        ptBR: 'Imposto deve ser 0 ou maior'
      }
    },
    margin: {
      positive: {
        en: 'Margin must be 0 or greater',
        ptBR: 'Margem deve ser 0 ou maior'
      }
    },
    labourCost: {
      positive: {
        en: 'Labour cost must be 0 or greater',
        ptBR: 'Custo de folha deve ser 0 ou maior'
      }
    },
    approvedWork: {
      positive: {
        en: 'Approved work must be 0 or greater',
        ptBR: 'Trabalho aprovado deve ser 0 ou maior'
      }
    },
    trainingBudget: {
      required: {
        en: 'Training budget is required',
        ptBR: 'Orçamento de treinamento é obrigatório'
      },
      positive: {
        en: 'Training budget must be greater than 0',
        ptBR: 'Orçamento de treinamento deve ser maior que 0'
      }
    },
    trainingActualCost: {
      positive: {
        en: 'Actual cost must be 0 or greater',
        ptBR: 'Custo real deve ser 0 ou maior'
      }
    },
    year: {
      required: {
        en: 'Year is required',
        ptBR: 'Ano é obrigatório'
      },
      invalid: {
        en: 'Year must be a valid number',
        ptBR: 'Ano deve ser um número válido'
      }
    }
  }
} as const

/**
 * Conflict error messages (409)
 * Resource was modified by another user
 */
export const CONFLICT_ERRORS = {
  en: 'This budget was modified by another user. Please refresh and try again.',
  ptBR: 'Este orçamento foi modificado por outro usuário. Por favor, atualize e tente novamente.',
  action: 'refresh'
} as const

/**
 * Timeout error messages
 * Request took too long (> 30 seconds)
 */
export const TIMEOUT_ERRORS = {
  en: 'The request timed out. Please try again.',
  ptBR: 'A requisição expirou. Por favor, tente novamente.',
  action: 'retry'
} as const

/**
 * Unknown error messages
 * Fallback for unrecognized errors
 */
export const UNKNOWN_ERRORS = {
  en: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
  ptBR: 'Ocorreu um erro inesperado. Por favor, tente novamente ou entre em contato com o suporte se o problema persistir.',
  action: 'retry-or-support'
} as const

/**
 * Error action types for button display
 */
export type ErrorAction =
  | 'retry'
  | 'refresh'
  | 'redirect-to-login'
  | 'show-toast'
  | 'retry-or-support'
  | 'none'

/**
 * Helper to get error message by locale
 */
export function getErrorMessage(
  errorMessages: { en: string; ptBR: string },
  locale: 'en' | 'pt-BR' = 'pt-BR'
): string {
  return locale === 'pt-BR' ? errorMessages.ptBR : errorMessages.en
}

/**
 * Map HTTP status codes to error messages
 */
export function getErrorMessageByStatusCode(
  statusCode: number,
  locale: 'en' | 'pt-BR' = 'pt-BR',
  context?: { year?: number; clientCode?: string }
): string {
  switch (statusCode) {
    case 401:
      return getErrorMessage(AUTHENTICATION_ERRORS, locale)
    case 403:
      return getErrorMessage(AUTHORIZATION_ERRORS, locale)
    case 404:
      if (context?.year && context?.clientCode) {
        return NOT_FOUND_ERRORS.clientBudget[locale](context.clientCode, context.year)
      }
      if (context?.year) {
        return NOT_FOUND_ERRORS.budget[locale](context.year)
      }
      return getErrorMessage(NOT_FOUND_ERRORS.generic, locale)
    case 409:
      return getErrorMessage(CONFLICT_ERRORS, locale)
    case 500:
    case 502:
    case 503:
    case 504:
      return getErrorMessage(SERVER_ERRORS, locale)
    default:
      if (statusCode >= 500) {
        return getErrorMessage(SERVER_ERRORS, locale)
      }
      if (statusCode >= 400) {
        return getErrorMessage(VALIDATION_ERRORS.generic, locale)
      }
      return getErrorMessage(UNKNOWN_ERRORS, locale)
  }
}

/**
 * Get error action by status code
 */
export function getErrorActionByStatusCode(statusCode: number): ErrorAction {
  switch (statusCode) {
    case 401:
      return 'redirect-to-login'
    case 403:
      return 'show-toast'
    case 404:
      return 'none'
    case 409:
      return 'refresh'
    case 500:
    case 502:
    case 503:
    case 504:
      return 'retry'
    default:
      if (statusCode >= 500) {
        return 'retry'
      }
      return 'retry-or-support'
  }
}
